import './agenda-notes-modal.scss';

import { useMutation } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useAgendaNote } from '../../../../data/data/agenda/use-agenda-notes';
import { BaseModal } from '../../../../data/data/types';
import {
  GetNoteDocument,
  MutateNoteDocument,
} from '../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { Button, ButtonType } from '../../../../elements/button/button';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';

export type AgendaNotesModalProps = BaseModal & {
  healthProviderId?: string | null;
  healthProfessionalId?: string | null;
  date?: string | null;
};

export function AgendaNotesModal({
  onCancel,
  onClose,
  onSaved,
  healthProviderId,
  healthProfessionalId,
  date,
}: AgendaNotesModalProps) {
  const intl = useIntl();
  const { showAlertMessage } = useAppContext();

  const [value, setValue] = useState<string | null>(null);
  const [initialValue, setInitialValue] = useState<string | null>(null);

  // get note
  const { data: note, loading: noteLoading } = useAgendaNote({
    healthProviderId,
    healthProfessionalId,
    date,
  });

  useEffect(() => {
    setValue(note);
    setInitialValue(note);
  }, [note]);

  const [mutateNote, { loading: mutateNoteLoading }] = useMutation(
    MutateNoteDocument,
    {
      refetchQueries: [GetNoteDocument],
    }
  );

  const onHandleCancel = useCallback(
    (_confirmed: boolean = false) => {
      onCancel?.();
      onClose?.();
    },
    [onCancel, onClose]
  );

  const canSave = useMemo(() => {
    if (noteLoading && mutateNoteLoading) {
      return false;
    }
    if (initialValue && value && initialValue === value) {
      return false;
    }
    return true;
  }, [initialValue, mutateNoteLoading, noteLoading, value]);

  const onHandleSubmit = useCallback(async () => {
    try {
      const result = await mutateNote({
        variables: {
          text: value,
          healthProviderId,
          healthProfessionalId,
          date,
        },
      });

      if (result.data?.noteMutate.boolean === true) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'agenda.note.success',
          }),
        });

        onSaved?.();

        onClose?.();
      } else {
        throw new Error('An unknown error has occurred in the server respond.');
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }

    onClose?.();
  }, [
    date,
    healthProfessionalId,
    healthProviderId,
    intl,
    mutateNote,
    onClose,
    onSaved,
    showAlertMessage,
    value,
  ]);

  /*
  const onChange = useCallback((editorValue: string) => {
    setValue(editorValue);
  }, []);
  */
  return (
    <Modal
      testId="agenda-notes-modal"
      title={<CustomFormattedMessage id="agenda.notes.title" />}
      width={ModalWidth.LARGE}
      onClose={onHandleCancel}
      buttons={
        <>
          <Button
            type={ButtonType.SECONDARY}
            inverted
            disabled={mutateNoteLoading || noteLoading}
            onClick={() => onHandleCancel(false)}
          >
            <CustomFormattedMessage id="common.cancel" />
          </Button>

          <Button onClick={onHandleSubmit} disabled={!canSave}>
            <CustomFormattedMessage id="common.save" />
          </Button>
        </>
      }
    >
      {/* TODO <TextEditor text={value} onChange={onChange} /> */}
    </Modal>
  );
}
